import axios from "axios";
import { rewriteMediaUrls } from "../utils/mediaUrl";
import cookieStorage from "./cookie-storage";
import breadcrumbCollector from "./breadcrumb-collector";
import errorReporter from "./error-reporter";

// Set a default timeout of 5 minutes for all requests (uploads can take longer)
// Individual requests can override this with their own timeout config
axios.defaults.timeout = 300000; // 5 minutes

// Enable connection keep-alive for better performance
axios.defaults.httpAgent = new (require('http').Agent)({
  keepAlive: true,
  keepAliveMsecs: 1000,
  maxSockets: 10
});

axios.defaults.httpsAgent = new (require('https').Agent)({
  keepAlive: true,
  keepAliveMsecs: 1000,
  maxSockets: 10
});

// Track all API responses as breadcrumbs
axios.interceptors.response.use(
  (response) => {
    breadcrumbCollector.addApiCall(
      response.config.method?.toUpperCase(),
      response.config.url,
      response.status,
    );
    // Serve S3 media through CloudFront (see utils/mediaUrl.js).
    rewriteMediaUrls(response.data);
    return response;
  },
  (error) => {
    // Error breadcrumbs handled in handleError
    return Promise.reject(error);
  }
);

class HttpService {
  static async get(url, config) {
    try {
      const response = await axios.get(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error; // Re-throw the error so it can be caught in the calling function
    }
  }

  static async post(url, body, config) {
    // Log tip-related requests
    if (url && url.includes('/payment/tip')) {
      console.log('[HTTP SERVICE] Tip request:', {
        timestamp: new Date().toISOString(),
        url: url,
        body: body,
        headers: config?.headers
      });
    }
    
    try {
      const response = await axios.post(url, body, config);
      
      // Log tip response
      if (url && url.includes('/payment/tip')) {
        console.log('[HTTP SERVICE] Tip response:', {
          timestamp: new Date().toISOString(),
          status: response.status,
          data: response.data
        });
      }
      
      return response.data;
    } catch (error) {
      // Log tip errors
      if (url && url.includes('/payment/tip')) {
        console.log('[HTTP SERVICE] Tip error:', {
          timestamp: new Date().toISOString(),
          error: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
      }
      
      this.handleError(error);
      throw error; // Re-throw the error
    }
  }

  static async put(url, body, config) {
    try {
      const response = await axios.put(url, body, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error; // Re-throw the error
    }
  }

  static async patch(url, body, config) {
    try {
      const response = await axios.patch(url, body, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error; // Re-throw the error
    }
  }

  static async delete(url, config) {
    try {
      const response = await axios.delete(url, config);
      return response.data;
    } catch (error) {
      this.handleError(error);
      throw error; // Re-throw the error
    }
  }

  // Centralized error handler
  static handleError(error) {
    if (!error.response) {
      console.error("Network error:", error);
      return;
    }

    const { status, data } = error.response;

    // Track failed API call as breadcrumb
    breadcrumbCollector.addApiCall(
      error.config?.method?.toUpperCase(),
      error.config?.url,
      status,
    );

    // Report 5xx server errors
    if (status >= 500) {
      errorReporter.captureError({
        message: data?.message || `Server error ${status}`,
        errorType: "HTTP5xx",
        severity: "error",
        endpoint: error.config?.url,
        httpStatus: status,
        httpMethod: error.config?.method?.toUpperCase(),
      });
    }

    // Handle JWT expiration or missing token. The API answers an expired or
    // rejected token with a 400 and "JWT expired, Please login again"; a tab
    // left open past its session used to keep polling with that dead token
    // (each live-stream card retries its snapshot every 30s).
    const authMessage = typeof data?.message === "string" ? data.message : "";
    if (status === 401 || authMessage === "JWT not available" || authMessage.startsWith("JWT expired")) {
      this.clearData();
    }

    // Handle staging environment restriction
    if (status === 403 && data?.code === 'STAGING_ACCESS_RESTRICTED') {
      this.clearData();
      window.location.replace('/staging-restricted');
      return;
    }

    // Handle server downtime or specific errors
    if (data?.message === "App is temporarily unavailable" || data?.code === "SERVER_DOWN") {
      console.log("Server is down.");
      this.serverDownScreen();
    }

    // Log the error for debugging
    console.error('API Error:', {
      url: error.config?.url,
      status: status,
      message: data?.message
    });
  }

  static clearData() {
    localStorage.clear();
    cookieStorage.deleteCookie("Authorization");
    setTimeout(() => {
      window.location = "/";
    }, 1000);
  }

  static serverDownScreen() {
    window.location.replace("/maintenance");
  }
}

export default HttpService;